import { useRef } from 'react';

/** The only field this needs — keeps it usable from any market-shaped payload. */
interface VolumeRanked {
    coin: string;
    dayNtlVlm?: string | null;
}

/**
 * 24h notional volume as a number. Hyperliquid sends it as a decimal string on
 * each asset context, and it can be absent on a market whose context has not
 * loaded — those sort last rather than as zero, so a market we simply have not
 * read yet is not presented as a dead one.
 */
export function dayVolumeUsd(market: VolumeRanked): number {
    const raw = market.dayNtlVlm;
    if (raw === null || raw === undefined) return -1;
    const value = Number(raw);
    return Number.isFinite(value) && value >= 0 ? value : -1;
}

/** Busiest first; ties and unknowns fall back to the coin name so the order is
 *  total and reproducible rather than dependent on the API's array order. */
export function compareByDayVolume(a: VolumeRanked, b: VolumeRanked): number {
    const diff = dayVolumeUsd(b) - dayVolumeUsd(a);
    return diff !== 0 ? diff : a.coin.localeCompare(b.coin);
}

/** the set of markets, as a value that changes only when a market is added or removed */
function marketSetKey(markets: readonly VolumeRanked[]): string {
    return markets
        .map((m) => m.coin)
        .sort()
        .join(',');
}

/**
 * Order markets by 24h volume, WITHOUT re-shuffling them under the user.
 *
 * The naive version — sorting on every render — reorders the list every time the
 * 30s refetch lands, because 24h volume moves continuously and adjacent markets
 * are often within a fraction of a percent of each other. In a ~230-row list that
 * a user is scrolling and tapping, a row that swaps places between the look and
 * the tap is a mis-click on a trading surface. It is the same fault we fixed in
 * the positions table: which row is where must not depend on a background poll.
 *
 * So the RANKING is computed from the set of markets, and recomputed only when
 * that set changes — a market listed or delisted. Prices, funding and volume keep
 * updating live in place; only the order is held still.
 *
 * Search and favourites still reorder on purpose, because those are the user's
 * own actions and they expect the list to respond.
 */
export function useVolumeOrder<T extends VolumeRanked>(
    markets: readonly T[],
): readonly T[] {
    const cache = useRef<{ key: string; rank: Map<string, number> } | null>(
        null,
    );

    const key = marketSetKey(markets);
    if (cache.current?.key !== key) {
        const rank = new Map<string, number>();
        [...markets]
            .sort(compareByDayVolume)
            .forEach((market, index) => rank.set(market.coin, index));
        cache.current = { key, rank };
    }

    const { rank } = cache.current;
    // A market absent from the ranking cannot happen (the key is derived from the
    // same list) but sorts last rather than throwing if it ever does.
    return [...markets].sort(
        (a, b) =>
            (rank.get(a.coin) ?? Number.MAX_SAFE_INTEGER) -
            (rank.get(b.coin) ?? Number.MAX_SAFE_INTEGER),
    );
}
